import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(null); // null = unknown, true/false = checked
  const [showOnboarding, setShowOnboarding] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      
      // Check profile completion status for authenticated users
      try {
        const statusRes = await axios.get(`${API_URL}/api/profile/completion-status`, {
          withCredentials: true
        });
        setProfileComplete(statusRes.data.profile_completion);
        
        // Show onboarding if profile incomplete and not admin
        if (!statusRes.data.profile_completion && !response.data.is_admin) {
          setShowOnboarding(true);
        }
      } catch (err) {
        console.error('Error checking profile completion:', err);
        setProfileComplete(false);
      }
    } catch (error) {
      setUser(null);
      setProfileComplete(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setProfileComplete(null);
    setShowOnboarding(false);
  };

  // Helper to check if user can participate (create posts/issues)
  const canParticipate = useCallback(() => {
    if (!user) return false;
    if (user.is_admin) return true; // Admins exempt
    return profileComplete === true;
  }, [user, profileComplete]);

  // Trigger onboarding modal
  const triggerOnboarding = useCallback(() => {
    if (user && !profileComplete) {
      setShowOnboarding(true);
    }
  }, [user, profileComplete]);

  // Called when onboarding completes
  const onOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    setProfileComplete(true);
    // Refresh user data
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      loading, 
      logout, 
      checkAuth,
      profileComplete,
      setProfileComplete,
      showOnboarding,
      setShowOnboarding,
      canParticipate,
      triggerOnboarding,
      onOnboardingComplete
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
